import { Column } from '@tanstack/react-table';
import { Check, ChevronDown } from 'lucide-react';
import pluralize from 'pluralize';
import { useState } from 'react';
import { cn } from '../../utils/cn';
import { Button } from '../button/button';
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from '../command-menu/command-menu-primitives';
import { Popover } from '../popover/popover';
import { SearchInputConfig } from './data-table';

type ColumnFilterProps<TData> = {
  column?: Column<TData>;
  label: string;
  search: SearchInputConfig;
};

export const ColumnFilter = <TData,>({ column, label, search = { show: true } }: ColumnFilterProps<TData>) => {
  const [searchTerm, setSearchTerm] = useState('');

  if (
    !column ||
    // Filter should not display when less than 2 options
    // i.e. if the only vendor available is "No Vendor" or "No category" it should not be displayed
    // i.e.2. if there is only one unique value, there is no point filtering
    column.getFacetedUniqueValues().size < 2
  ) {
    return null;
  }

  const columnUniqueValues = Array.from(column.getFacetedUniqueValues().keys()).filter((value) => Boolean(value));
  const selectedValues = new Set(column?.getFilterValue() as string[]);
  const visibleLabel = selectedValues.size ? pluralize(label.toLowerCase(), selectedValues.size, true) : label;

  return (
    <Popover
      triggerElement={
        <Button
          variant="secondary"
          className="group"
          iconRight={<ChevronDown className="h-4 w-4" />}
          iconRightClassName="transition-transform duration-200 ease-out group-data-[state=open]:rotate-180"
        >
          {visibleLabel}
        </Button>
      }
      content={
        <Command className="flex flex-col gap-4">
          <div className={cn('flex flex-col gap-4 px-5 pt-5', !search.show && 'pt-2')}>
            {search.show && (
              <CommandInput
                placeholder={search.placeholder ?? `Search ${column.id}`}
                // "Controlled" value so we can reset it on Reset button click (see footer prop)
                value={searchTerm}
                onValueChange={setSearchTerm}
              />
            )}

            <CommandList>
              <CommandGroup>
                {columnUniqueValues.map((uniqueValue) => {
                  const isSelected = selectedValues.has(uniqueValue);

                  return (
                    <CommandItem
                      key={uniqueValue}
                      onSelect={() => {
                        if (isSelected) {
                          selectedValues.delete(uniqueValue);
                        } else {
                          selectedValues.add(uniqueValue);
                        }

                        const filterValues = Array.from(selectedValues);
                        column?.setFilterValue(filterValues.length ? filterValues : undefined);
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className={cn(
                            'flex h-4 w-4 items-center justify-center rounded-md border p-[1px]',
                            isSelected ? 'border-primary bg-primary text-black' : 'opacity-50 [&_svg]:invisible',
                          )}
                        >
                          <Check className="w-full" strokeWidth={1.4} />
                        </div>

                        <p>{uniqueValue}</p>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </div>
        </Command>
      }
      footerElement={
        <Button
          variant="outline"
          className="w-full"
          onClick={() => {
            column.setFilterValue(undefined);
            setSearchTerm('');
          }}
        >
          Reset
        </Button>
      }
      align="start"
    />
  );
};
